/**
 * MobileMenu — backdrop + slide-down nav panel for screens below `md`.
 * Kept mounted while on mobile so the open/close transform animates.
 *
 * Rendered through a portal to <body> rather than inside the sticky <header>.
 * The body scroll-lock (useBodyScrollLock) sets `body { position: fixed }`
 * while open; a `position: fixed` overlay nested inside a `position: sticky`
 * ancestor gets displaced by that (header collapses, taking the close button
 * and backdrop with it, and the backdrop-blur loses its backdrop). Portaling
 * to <body> keeps the overlay anchored to the viewport regardless.
 */
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { LogIn, LogOut, User } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useTheme } from '../../../context/ThemeContext';
import { useAuthModal } from '../../../context/AuthModalContext';
import ThemeToggle from '../../ui/ThemeToggle';
import { NAV_ITEMS, isNavActive, navLinkClasses } from './navConfig';
import NavIcon from './NavIcon';
import UserAvatar from './UserAvatar';
import { useNavbarUser } from './useNavbarUser';

const MOBILE_ICON_SIZE = 'w-[18px] h-[18px]';

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}

export default function MobileMenu({ open, onClose }: MobileMenuProps) {
  const location = useLocation();
  const { theme } = useTheme();
  const isSukhasan = theme === 'sukhasan';
  const { openAuthModal } = useAuthModal();
  const {
    isAuthenticated,
    loading,
    profile,
    displayName,
    photoURL,
    initials,
    email,
    handleSignOut,
  } = useNavbarUser();

  const divider = <div className="my-2 h-px" style={{ backgroundColor: 'var(--glass-border)' }} />;

  return createPortal(
    <>
      {/* Backdrop (tap to close) — full-screen so backdrop-blur has the page
          behind it. Sits below the header (z-40 < header z-50). */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden transition-opacity duration-200',
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-down panel (GPU composited) */}
      <div
        className="fixed inset-x-0 z-50 md:hidden will-change-transform border-b shadow-lg shadow-black/5 backdrop-blur-xl"
        style={{
          backgroundColor: 'var(--tabbar-glass-bg)',
          borderColor: 'var(--glass-border)',
          top: '3.5rem',
          transform: open ? 'translateY(0)' : 'translateY(-100%)',
          opacity: open ? 1 : 0,
          transition: open
            ? 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1), opacity 150ms ease'
            : 'transform 180ms cubic-bezier(0.4, 0, 1, 1), opacity 120ms ease',
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        <nav className="px-3 pb-4 pt-2 max-h-[calc(100dvh-3.5rem)] overflow-y-auto overscroll-contain">
          {/* Nav links */}
          <div className="space-y-0.5">
            {NAV_ITEMS.map((item) => {
              if (item.kind === 'link') {
                const active = isNavActive(location.pathname, item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={onClose}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                      navLinkClasses(active, isSukhasan, 'mobile'),
                    )}
                  >
                    <NavIcon
                      src={item.imgSrc}
                      icon={item.icon}
                      active={active}
                      invert={isSukhasan}
                      iconClass={item.iconClass}
                      sizeClass={MOBILE_ICON_SIZE}
                    />
                    {item.label}
                  </Link>
                );
              }

              // Group: section header + indented children
              return (
                <div key={item.label} className="pt-1">
                  <div className="flex items-center gap-3 px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-tx-muted">
                    <NavIcon
                      src={item.imgSrc}
                      icon={item.icon}
                      invert={isSukhasan}
                      iconClass={item.iconClass}
                      sizeClass={MOBILE_ICON_SIZE}
                      imgClassName="opacity-60"
                    />
                    {item.label}
                  </div>
                  {item.children.map((child) => {
                    const childActive = isNavActive(location.pathname, child.href);
                    return (
                      <Link
                        key={child.href}
                        to={child.href}
                        onClick={onClose}
                        className={cn(
                          'flex items-center gap-3 rounded-lg pl-9 pr-3 py-2.5 text-sm font-medium transition-colors',
                          navLinkClasses(childActive, isSukhasan, 'mobile'),
                        )}
                      >
                        <NavIcon
                          src={child.imgSrc}
                          icon={child.icon}
                          active={childActive}
                          invert={isSukhasan}
                          iconClass={child.iconClass}
                          sizeClass={MOBILE_ICON_SIZE}
                        />
                        {child.label}
                      </Link>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {divider}

          {/* Appearance / theme toggle row */}
          <div className="flex items-center justify-between rounded-lg px-3 py-2.5">
            <span className="text-sm font-medium text-tx-secondary">
              Appearance
            </span>
            <ThemeToggle />
          </div>

          {divider}

          {loading || (isAuthenticated && !profile) ? (
            /* Skeleton while auth or profile is loading */
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="skeleton-shimmer w-8 h-8 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="skeleton-shimmer h-3 w-24 rounded" />
                <div className="skeleton-shimmer h-2.5 w-32 rounded" />
              </div>
            </div>
          ) : isAuthenticated && profile ? (
            <div className="space-y-0.5">
              {/* Compact user row */}
              <div className="flex items-center gap-3 px-3 py-2">
                <UserAvatar
                  photoURL={photoURL}
                  displayName={displayName}
                  initials={initials}
                  isSukhasan={isSukhasan}
                  sizeClass="w-8 h-8"
                  textClass="text-xs"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate text-tx-primary">
                    {displayName}
                  </p>
                  <p className="text-xs truncate text-tx-muted">
                    {email}
                  </p>
                </div>
              </div>
              <Link
                to="/profile"
                onClick={onClose}
                className="flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-sm font-medium text-tx-secondary hover:bg-elevated"
              >
                <User size={18} strokeWidth={1.75} />
                My Profile
              </Link>
              <button
                onClick={() => {
                  onClose();
                  handleSignOut();
                }}
                className="flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-sm font-medium text-[#EF4444] hover:bg-elevated"
              >
                <LogOut size={18} strokeWidth={1.75} />
                Sign Out
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                onClose();
                openAuthModal();
              }}
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium w-full text-accent-500 hover:bg-elevated"
            >
              <LogIn size={18} strokeWidth={1.75} />
              Sign In
            </button>
          )}
        </nav>
      </div>
    </>,
    document.body,
  );
}
